import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { DATABASE } from '../Config/firebase';

/**
 * Default fund rules (fallback if Firestore document doesn't exist)
 */
export const DEFAULT_FUND_RULES = {
  version: "1.0",
  active: true,
  
  commission: {
    positive_performance: 30,
    negative_performance: 0
  },
  
  deposit_rules: {
    cutoff_hour_utc: 21,
    same_day_until: "21:00",
    next_day_after: "21:01",
    trading_starts: "D+1",
    approval_window_hours: 24
  },
  
  withdrawal_rules: {
    trading_stops: "D0",
    large_withdrawal_threshold: 80,
    
    conditional_offer: {
      enabled: true,
      default_time_minutes: 60,
      allow_custom_time: true,
      min_time_minutes: 15,
      max_time_minutes: 240,
      negative_performance_applies_to: "full_account"
    }
  },

  // Phase 2 — Auto-Pay small withdrawals (disabled by default)
  auto_pay: {
    enabled: false,
    min_amount: 100,
    max_amount: 0,
    daily_cap: 0,
    accept_delay_minutes: 720,
    pay_delay_minutes: 360,
    max_window_hours: 24,
    // Phase 2 — Operating window (business hours). Off by default: purely
    // additive gate in the cron. When enabled, auto-pay only accepts and pays
    // between operating_start_hour and operating_end_hour in operating_timezone.
    operating_window_enabled: false,
    operating_start_hour: 9,
    operating_end_hour: 21,
    operating_timezone: "Europe/London",
    supported_assets: ["USDT", "USDC"],
    supported_networks: ["POLYGON"],
    liquidity_wallets: {
      USDT_POLYGON: "",
      USDC_POLYGON: "",
      USDT_BSC: "",
      USDC_BSC: "",
    },
    kyc_whitelist_uids: [],
    // Phase 2 — Bybit on-chain payout (off until API keys + address book configured)
    exchange_enabled: false,
    exchange_provider: "bybit"
  },
  
  monthly_closing: {
    day_calculation_method: "last_day_of_month",
    performance_calculation: "daily_weighted",
    commission_application: "on_positive_only"
  }
};

/**
 * Fetch active fund rules from Firestore
 * 
 * @returns {Promise<Object>} Fund rules object
 */
export async function getFundRules() {
  try {
    const rulesRef = doc(DATABASE, 'fund_rules', 'active');
    const rulesSnap = await getDoc(rulesRef);
    
    if (rulesSnap.exists()) {
      return rulesSnap.data();
    }
    
    // If no rules exist, return defaults and create the document
    console.warn('Fund rules not found, using defaults');
    await createDefaultFundRules();
    return DEFAULT_FUND_RULES;
  } catch (error) {
    console.error('Error fetching fund rules:', error);
    return DEFAULT_FUND_RULES;
  }
}

/**
 * Update fund rules
 * 
 * @param {Object} updates - Partial or full fund rules object
 * @param {string} updatedBy - Email of admin making the change
 * @returns {Promise<void>}
 */
export async function updateFundRules(updates, updatedBy = 'admin') {
  try {
    const rulesRef = doc(DATABASE, 'fund_rules', 'active');
    const currentRules = await getFundRules();
    
    // Merge updates with current rules
    const newRules = {
      ...currentRules,
      ...updates,
      updated_at: Timestamp.now(),
      updated_by: updatedBy,
      version: incrementVersion(currentRules.version)
    };
    
    // Save updated rules
    await setDoc(rulesRef, newRules);
    
    // Archive old version
    await archiveFundRules(currentRules);
    
    return newRules;
  } catch (error) {
    console.error('Error updating fund rules:', error);
    throw error;
  }
}

/**
 * Create default fund rules document
 * 
 * @returns {Promise<void>}
 */
async function createDefaultFundRules() {
  try {
    const rulesRef = doc(DATABASE, 'fund_rules', 'active');
    await setDoc(rulesRef, {
      ...DEFAULT_FUND_RULES,
      created_at: Timestamp.now(),
      updated_at: Timestamp.now(),
      updated_by: 'system_init'
    });
  } catch (error) {
    console.error('Error creating default fund rules:', error);
  }
}

/**
 * Archive old version of fund rules
 * 
 * @param {Object} rules - Rules to archive
 * @returns {Promise<void>}
 */
async function archiveFundRules(rules) {
  try {
    const timestamp = Date.now();
    const archiveRef = doc(DATABASE, 'fund_rules_history', `v${rules.version}_${timestamp}`);
    await setDoc(archiveRef, {
      ...rules,
      archived_at: Timestamp.now()
    });
  } catch (error) {
    console.error('Error archiving fund rules:', error);
  }
}

/**
 * Increment version string (e.g., "1.0" -> "1.1", "1.9" -> "2.0")
 * 
 * @param {string} version - Current version
 * @returns {string} New version
 */
function incrementVersion(version) {
  const parts = version.split('.');
  const major = parseInt(parts[0]) || 1;
  const minor = parseInt(parts[1]) || 0;
  
  if (minor < 9) {
    return `${major}.${minor + 1}`;
  }
  return `${major + 1}.0`;
}

/**
 * Get commission rate based on performance sign
 * 
 * @param {number} performance - Performance value
 * @param {Object} rules - Fund rules object (optional, will fetch if not provided)
 * @returns {Promise<number>} Commission rate (%)
 */
export async function getCommissionRate(performance, rules = null) {
  if (!rules) {
    rules = await getFundRules();
  }
  
  return performance >= 0 
    ? rules.commission.positive_performance 
    : rules.commission.negative_performance;
}

/**
 * Check if withdrawal amount triggers conditional offer
 * 
 * @param {number} withdrawalAmount - Amount to withdraw
 * @param {number} currentBalance - Current net balance
 * @param {Object} rules - Fund rules object (optional)
 * @returns {Promise<boolean>} True if conditional offer should be triggered
 */
export async function shouldTriggerConditionalOffer(withdrawalAmount, currentBalance, rules = null) {
  if (!rules) {
    rules = await getFundRules();
  }
  
  if (!rules.withdrawal_rules.conditional_offer.enabled) {
    return false;
  }
  
  const percentage = (withdrawalAmount / currentBalance) * 100;
  return percentage >= rules.withdrawal_rules.large_withdrawal_threshold;
}

const fundRulesService = {
  getFundRules,
  updateFundRules,
  getCommissionRate,
  shouldTriggerConditionalOffer,
  DEFAULT_FUND_RULES
};

export default fundRulesService;

